import * as React from 'react'
import { cn } from '@/lib/utils'

interface TypographyProps {
  children: React.ReactNode
  className?: string | null
  noMargin?: boolean
  small?: boolean
}

interface HeadingProps extends TypographyProps {
  serif?: boolean
}

export function TypographyH1({ children, serif = false, className = null, noMargin = false }: HeadingProps) {
  return (
    <h1
      className={cn(
        'typography-heading text-foreground text-left scroll-m-20 text-4xl md:text-5xl font-bold !leading-snug tracking-tight text-balance !mt-0',
        serif && 'font-serif font-medium tracking-normal',
        className
      )}
    >
      {children}
    </h1>
  )
}

export function TypographyH2({ children, serif = false, className = null, noMargin = false }: HeadingProps) {
  return (
    <h2
      className={cn(
        'typography-heading text-foreground w-full text-left scroll-m-20 text-3xl md:text-4xl font-bold !leading-snug tracking-tight !mt-0',
        !noMargin && 'mt-8 md:mt-12',
        serif && 'font-serif font-medium tracking-normal',
        className
      )}
    >
      {children}
    </h2>
  )
}

export function TypographyH3({ children, serif = false, className = null, noMargin = false }: HeadingProps) {
  return (
    <h3
      className={cn(
        'typography-heading text-foreground text-left scroll-m-20 text-2xl md:text-3xl font-bold !leading-snug tracking-tight !mt-0',
        !noMargin && 'mt-8 md:mt-12',
        serif && 'font-serif font-medium tracking-normal',
        className
      )}
    >
      {children}
    </h3>
  )
}

export function TypographyH4({ children, serif = false, className = null, noMargin = false }: HeadingProps) {
  return (
    <h4
      className={cn(
        'typography-heading text-foreground text-left scroll-m-20 text-xl md:text-2xl font-bold !leading-snug tracking-tight !mt-0',
        !noMargin && 'mt-8 md:mt-12',
        serif && 'font-serif font-medium tracking-normal',
        className
      )}
    >
      {children}
    </h4>
  )
}

export function TypographyP({ children, className = null, noMargin = false, small = false }: TypographyProps) {
  return (
    <div
      className={cn(
        'text-foreground font-normal text-left !leading-relaxed text-pretty',
        !noMargin && '[&:not(:first-child)]:mt-4',
        small ? "text-sm" : "text-base",
        className
      )}
    >
      {children}
    </div>
  )
}

export function TypographyLead({ children, className = null, noMargin = false }: TypographyProps) {
  return (
    <p
      className={cn(
        'text-foreground font-semibold text-left leading-snug text-xl lg:text-xl',
        !noMargin && 'mt-8',
        className
      )}
    >
      {children}
    </p>
  )
}

export function TypographyLarge({ children, className = null, noMargin = false }: TypographyProps) {
  return (
    <div
      className={cn(
        'text-foreground font-[250] text-left leading-snug text-xl lg:text-xl',
        !noMargin && '[&:not(:first-child)]:mt-4',
        className
      )}
    >
      {children}
    </div>
  )
}

export function TypographyBlockquote({ children, className = null, noMargin = false }: TypographyProps) {
  return (
    <blockquote
      className={cn(
        'text-foreground text-left leading-[1.8] py-1 border-l-4 border-border pl-4 md:pl-6 italic',
        !noMargin && 'mt-4',
        className
      )}
    >
      {children}
    </blockquote>
  )
}

export function TypographyCode({ children, className = null }: TypographyProps) {
  return (
    <code className={cn(
      "text-foreground bg-secondary rounded-sm px-2 py-0.5 font-mono border text-xs font-medium whitespace-nowrap",
      className
    )}>
      {children}
    </code>
  )
}

export function TypographyList({ children, className = null, noMargin = false }: TypographyProps) {
  return (
    <ul
      className={cn(
        'text-foreground ml-9 md:ml-9 list-disc [&>li]:mt-3',
        !noMargin && 'mt-3',
        className
      )}
    >
      {children}
    </ul>
  )
}

export function TypographyListItem({ children, className = null, small = false }: TypographyProps) {
  return (
    <li className={cn(
      "text-foreground !leading-relaxed text-pretty",
      small ? "text-sm" : "text-base",
      className
    )}>
      {children}
    </li>
  )
}

export function TypographyUnderlined({ children, className = null }: TypographyProps) {
  return (
    <span className={cn(
      "text-foreground font-semibold underline underline-offset-6",
      className
    )}>
      {children}
    </span>
  )
}

export function TypographyLink({ children, className = null }: TypographyProps) {
  return (
    <span className={cn(
      "text-foreground font-semibold underline underline-offset-6 hover:opacity-80",
      className
    )}>
      {children}
    </span>
  )
}

export function TypographyLabel({ children, className = null }: TypographyProps) {
  return (
    <div className={cn(
      "text-foreground text-[12px] uppercase font-bold tracking-widest",
      className
    )}>
      {children}
    </div>
  )
}

export function TypographySmallLabel({ children, className = null }: TypographyProps) {
  return (
    <div className={cn(
      "text-muted-foreground text-[10px] uppercase font-semibold tracking-widest",
      className
    )}>
      {children}
    </div>
  )
}
